import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@mantine/hooks";
import type { ListParams, PaginatedResult } from "../types/api";

interface Options<T> {
  // Stable key prefix for react-query (e.g. ["drivers"]).
  queryKey: unknown[];
  // Fetcher receives the resolved list params (page/limit/search + filters).
  fetcher: (params: ListParams) => Promise<PaginatedResult<T>>;
  limit?: number;
  // Extra filter params merged into the request + query key.
  filters?: Record<string, string | number | boolean | undefined>;
}

// Shared list controller: page state, debounced search, react-query wiring.
export function usePaginatedList<T>({
  queryKey,
  fetcher,
  limit = 20,
  filters = {},
}: Options<T>) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);

  const query = useQuery({
    queryKey: [...queryKey, page, debouncedSearch, filters],
    queryFn: () =>
      fetcher({ page, limit, search: debouncedSearch || undefined, ...filters }),
    placeholderData: keepPreviousData,
  });

  function onSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  return {
    page,
    setPage,
    search,
    onSearchChange,
    result: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}
